import _ from 'lodash';

const LOOSE_MATCH_BOOST = 1;
const STRICT_MATCH_BOOST = 5;

type WeightedSearchItem = {
  name: string;
  code: string;
  alternativeSpellings?: string;
  relevancyBooster?: number;
};

type FormattedWeightedSearchItem = WeightedSearchItem & {
  matchString: string;
  words: string[];
};

const splitWords = (s = '') => s.split(/\s+/);

const formatItems = _.memoize(
  (items: WeightedSearchItem[]): FormattedWeightedSearchItem[] => {
    return items.map(item => {
      const words = splitWords(item.name)
        .concat(item.code)
        .concat(item.alternativeSpellings || '');
      const altSpellings = item.alternativeSpellings || '';

      const matchString = _.trim(`${item.name} ${item.code} ${altSpellings}`);

      return {
        words,
        matchString,
        ...item,
      };
    });
  }
);

/*
 Fuzzy search for value in items.

 Item name takes precedence, but items can have alternate spellings.
 Item code is always the first alternate spelling.

 You can force items to show up earlier in the results by giving them
 a relevancyBooster score that is greater than 0. (Or, less than 0 if
 you want have them show up later in the results.)

 Example item:

 {
   name: 'United States',
   code: 'US',
   alternativeSpellings: 'USA United States of America',
   relevancyBooster: 3.5
 }
*/
const weightedSearch = (value: string, items: Array<WeightedSearchItem>) => {
  value = _.trim(value);
  const formattedItems = formatItems(items);
  const parts = value.split(/\s+/);

  const regexps = _.map(parts, part => new RegExp(_.escapeRegExp(part), 'i'));

  const strictRegexps = _.map(
    parts,
    part => new RegExp(`^${_.escapeRegExp(part)}`, 'i')
  );

  const scoredMatches = _.map(formattedItems, item => {
    const partialMatches = _.filter(regexps, re => re.test(item.matchString));

    const hasStrictMatch = _.some(item.words, word => {
      return _.some(strictRegexps, re => re.test(word));
    });

    if (parts.length !== partialMatches.length) {
      return {
        score: -1,
        ...item,
      };
    }

    const boosterScore = item.relevancyBooster || 0;
    const strictScore = hasStrictMatch ? STRICT_MATCH_BOOST : 0;
    const score =
      partialMatches.length * LOOSE_MATCH_BOOST + boosterScore + strictScore;
    return {
      score,
      ...item,
    };
  });

  return scoredMatches
    .filter(item => item.score >= 0)
    .sort((a, b) => b.score - a.score);
};

export default weightedSearch;
